import { useState, useRef, useCallback } from 'react';
import { adminApi } from '../../api/adminApi';
import { downloadJson } from '../../utils/downloadJson';
import { useLogger, WebComponentCode } from '@shared-ui/logging';

// ── Types ────────────────────────────────────────────────────────────

export interface ImportExportStatus {
  type: 'success' | 'error';
  message: string;
}

export interface ImportPopup {
  title: string;
  details: string[];
}

// `parseUploadedFile` (lenient JSON + ZIP) lives in shared-ui now so the
// teacher-app wrapper around `ImportQuestionsDialog` can use it too. Import
// it from `@shared-ui/import-questions-dialog` if you need it here.

// ── Hook ─────────────────────────────────────────────────────────────

/**
 * Export-only hook for the questions page. Import is owned by
 * `ImportQuestionsDialog`, which runs the dry-run → review → confirm flow
 * end-to-end on its own state. Named `useQuestionImportExport` for historical
 * reasons; the filename still hosts `parseUploadedFile` used by the dialog.
 */
export function useQuestionImportExport() {
  const logger = useLogger(WebComponentCode.WEB_UI, 'question-import-export');
  const currentFiltersRef = useRef<Record<string, string | number>>({});

  const [status, setStatus] = useState<ImportExportStatus | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportStep, setExportStep] = useState('');
  const [popup, setPopup] = useState<ImportPopup | null>(null);

  const clearStatus = useCallback(() => setStatus(null), []);
  const clearPopup = useCallback(() => setPopup(null), []);

  const setFilters = useCallback((f: Record<string, string | number>) => {
    currentFiltersRef.current = f;
  }, []);

  const handleExport = useCallback(() => {
    setShowExportDialog(true);
  }, []);

  const runExport = useCallback(async (embedImages: boolean) => {
    setShowExportDialog(false);
    setIsExporting(true);
    try {
      const { maxCount: _, ...activeFilters } = currentFiltersRef.current;
      setExportStep(embedImages ? 'Exporting with images…' : 'Exporting questions…');

      logger.info('Exporting questions', { filters: activeFilters, embedImages });
      const result = await adminApi.exportQuestions(activeFilters, embedImages);
      logger.info('Export result', { questionCount: result.questions.length, hasImages: !!result.images, warningCount: result.warnings?.length || 0 });

      if (result.warnings && result.warnings.length > 0) {
        setPopup({ title: 'Export Warnings', details: result.warnings });
      }

      downloadJson(
        { questions: result.questions, ...(result.images ? { images: result.images } : {}), ...(result.warnings?.length ? { warnings: result.warnings } : {}) },
        `questions-${new Date().toISOString().split('T')[0]}.json`,
      );

      const imageMsg = result.images ? ` (with ${Object.keys(result.images).length} embedded images)` : '';
      const warningMsg = result.warnings?.length ? ` — ${result.warnings.length} warning(s)` : '';
      setStatus({ type: 'success', message: `Exported ${result.questions.length} questions${imageMsg}${warningMsg}` });
      setTimeout(() => setStatus(null), 5000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Question export failed', error);
      setStatus({ type: 'error', message: `Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
      setExportStep('');
    }
  }, [logger]);

  return {
    status,
    clearStatus,
    popup,
    clearPopup,
    isExporting,
    exportStep,
    handleExport,
    showExportDialog,
    onExportChoice: runExport,
    onExportCancel: useCallback(() => setShowExportDialog(false), []),
    setFilters,
  };
}
